import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import moment from 'moment';
import DataTable from '../util/DataTable';

interface RecalcLogEntry {
  id: number;
  userId: number;
  changedById: number | null;
  trigger: string;
  toilCount: number;
  leaveCount: number;
  details: any[] | null;
  createdAt: string;
  userFirstName?: string; userLastName?: string;
  byFirstName?: string; byLastName?: string;
}

const RecalcLog: React.FC = () => {
  const [rows, setRows] = useState<RecalcLogEntry[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('hr-recalc/log');
        setRows(Array.isArray(data) ? data : []);
      } catch { /* глобальный interceptor */ }
    })();
  }, []);

  const nameOf = (f?: string, l?: string, id?: number | null) =>
    (f || l) ? `${f ?? ''} ${l ?? ''}`.trim() : (id ? `#${id}` : '—');

  const columns = [
    {
      key: 'createdAt', label: t('Date'), sortable: true, filterType: 'daterange' as const,
      sortExtract: (r: RecalcLogEntry) => new Date(r.createdAt).getTime(),
      filterExtract: (r: RecalcLogEntry) => r.createdAt ?? '',
      render: (r: RecalcLogEntry) => <span className="text-[12px] text-[var(--text-color-calendar)]">{moment(r.createdAt).format('DD.MM.YYYY HH:mm')}</span>,
      width: 140,
    },
    {
      key: 'employee', label: t('User'), filterType: 'text' as const,
      filterExtract: (r: RecalcLogEntry) => nameOf(r.userFirstName, r.userLastName, r.userId),
      render: (r: RecalcLogEntry) => <span className="text-[13px] text-[var(--text-color)]">{nameOf(r.userFirstName, r.userLastName, r.userId)}</span>,
      width: 160,
    },
    {
      key: 'changedBy', label: t('ChangedBy') || 'Changed by', filterType: 'text' as const,
      filterExtract: (r: RecalcLogEntry) => nameOf(r.byFirstName, r.byLastName, r.changedById),
      render: (r: RecalcLogEntry) => <span className="text-[12px] text-[var(--text-color-calendar)]">{nameOf(r.byFirstName, r.byLastName, r.changedById)}</span>,
      width: 160,
    },
    {
      key: 'trigger', label: t('LogTrigger') || 'Trigger', filterType: 'select' as const,
      filterOptions: [{ value: 'manual', label: 'manual' }, { value: 'on_change', label: 'on_change' }],
      filterExtract: (r: RecalcLogEntry) => r.trigger ?? '',
      render: (r: RecalcLogEntry) => <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: 'var(--btn-user-form)', color: 'var(--text-color-calendar)' }}>{r.trigger}</span>,
      width: 110,
    },
    {
      key: 'toilCount', label: 'TOIL', sortable: true, align: 'center' as const,
      sortExtract: (r: RecalcLogEntry) => r.toilCount,
      render: (r: RecalcLogEntry) => <span className="text-[13px] font-semibold">{r.toilCount}</span>,
      width: 70,
    },
    {
      key: 'leaveCount', label: t('HrLeave') || 'Leave', sortable: true, align: 'center' as const,
      sortExtract: (r: RecalcLogEntry) => r.leaveCount,
      render: (r: RecalcLogEntry) => <span className="text-[13px] font-semibold">{r.leaveCount}</span>,
      width: 70,
    },
  ];

  const renderExpanded = (r: RecalcLogEntry) => (
    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {(!r.details || !r.details.length) ? (
        <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>{t('Empty')}</span>
      ) : r.details.map((d: any, i: number) => (
        <div key={i} style={{ fontSize: 12, color: 'var(--text-color-calendar)', fontVariantNumeric: 'tabular-nums' }}>
          <b style={{ textTransform: 'uppercase', fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{d.type}</b>{' '}
          {d.date || d.period}{' · '}
          {d.type === 'toil'
            ? <>sum <span style={{ color: '#ef4444' }}>{d.before?.sum}</span> → <span style={{ color: '#059669' }}>{d.after?.sum}</span> (h/wk {d.before?.hoursPerWeek}→{d.after?.hoursPerWeek})</>
            : <>{t('Hours')} <span style={{ color: '#ef4444' }}>{d.before?.hours}h/{d.before?.days}d</span> → <span style={{ color: '#059669' }}>{d.after?.hours}h/{d.after?.days}d</span></>}
        </div>
      ))}
    </div>
  );

  return (
    <DataTable
      rows={rows}
      getKey={(r) => r.id}
      columns={columns}
      renderExpanded={renderExpanded}
      csvFilename="recalc-log.csv"
      storageKey="recalc-log-table"
      emptyText={t('Empty')}
    />
  );
};

export default RecalcLog;
